import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import { useEffect } from 'react';
import { z } from 'zod';

import { useCRMAudienceReportMetadata } from '@/api/audience';
import { withFieldGroup } from '@/components/Form';
import { FieldDescription, FieldLabel } from '@/components/Form/fields';
import { ReportName } from '@/components/ReportName';
import { Text } from '@/components/typography';
import { getMessageByCode } from '@/error/messages';
import { INTEGRATION_ERROR_CODES } from '@/error/messages/integration-standard-errors';
import { isSalesforceReportUrlValid } from '@/helper/validator';
import useDebounce from '@/hooks/useDebounce';

type Props = {
  isCreatingAudience?: boolean;
};

const getSalesforceUrlFieldSchema = () =>
  z
    .string()
    .trim()
    .min(1, t`Salesforce Report Link is required`)
    .refine(
      (value) => isSalesforceReportUrlValid(value),
      getMessageByCode(INTEGRATION_ERROR_CODES.SALESFORCE_INVALID_URL)!,
    );

const ImportCRMAudienceSalesforceForm = withFieldGroup({
  defaultValues: {
    salesforce_url: '',
  },
  props: {
    isCreatingAudience: false,
  } as Props,
  render: function Render({ group, isCreatingAudience = false }) {
    const salesforceUrlFieldSchema = getSalesforceUrlFieldSchema();
    const reportURL = useStore(group.store, (state) => state.values['salesforce_url']);
    const debouncedReportURL = useDebounce(reportURL, 1000);
    const {
      reportMetadata,
      isLoading: isReportLoading,
      error: reportError,
    } = useCRMAudienceReportMetadata(
      isSalesforceReportUrlValid(debouncedReportURL) ? debouncedReportURL : undefined,
    );
    const reportErrorMessage = reportError?.getFirstError?.().message;

    useEffect(() => {
      group.validateField('salesforce_url', 'change');
    }, [debouncedReportURL, group, reportErrorMessage]);

    return (
      <div className="flex flex-col gap-4">
        <Text variant="body1">
          <Trans>
            <strong>Note: </strong>Your Salesforce Accounts report must include the "
            <strong>Website</strong>" field as a column, and must not contain grouped fields.
          </Trans>
        </Text>
        <group.AppField
          name="salesforce_url"
          validators={{
            onDynamic: salesforceUrlFieldSchema,
            onChangeAsync: async ({ value }) => {
              if (reportErrorMessage && value.trim() === debouncedReportURL) {
                return {
                  message: reportErrorMessage,
                };
              }
            },
          }}
        >
          {(field) => (
            <field.Field>
              <FieldLabel htmlFor="salesforce-report-link">
                <Trans>Salesforce Report Link</Trans>
              </FieldLabel>
              <FieldDescription>
                <Trans>
                  The url should be in the format of{' '}
                  <code>
                    https://&lt;your-subdomain&gt;.lightning.force.com/lightning/r/Report/&lt;report-id&gt;/view
                  </code>
                </Trans>
              </FieldDescription>
              <field.TextInput
                id="salesforce-report-link"
                disabled={isCreatingAudience}
                autoFocus
              />
              {(reportMetadata || isReportLoading) && (
                <ReportName reportName={reportMetadata?.name} isLoading={isReportLoading} />
              )}
              <field.Error />
            </field.Field>
          )}
        </group.AppField>
      </div>
    );
  },
});

export default ImportCRMAudienceSalesforceForm;
